import { useCallback, type MouseEvent } from 'react';
import { createStore, useStore } from './store';

// The address bar is the source of truth for where you are: the view, an open record or the admin console.
// That keeps the back button, refreshes and shared links working everywhere.

export type ViewId = 'today' | 'inbox' | 'people' | 'companies' | 'deals' | 'finder' | 'activity' | 'bin';
export type RecordType = 'person' | 'company';

export interface RecordRef {
  type: RecordType;
  id: string;
  /** The record tab to show, such as "emails" or "history". */
  tab?: string;
  /** Something inside the record to bring into view and highlight, such as a deal id. */
  focus?: string;
}

export interface Route {
  view: ViewId;
  /** Path segments after the view, for example a thread id in /inbox/<id>. */
  sub: string[];
  record: RecordRef | null;
  /** The open admin console section, or null. */
  admin: string | null;
  /** View-specific query parameters (filters, selected search and so on). */
  params: Record<string, string>;
}

export const VIEWS: ViewId[] = ['today', 'inbox', 'people', 'companies', 'deals', 'finder', 'activity', 'bin'];
const RESERVED = new Set(['record', 'tab', 'focus', 'admin', 'auth_error']);

function parseLocation(): Route {
  const parts = window.location.pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const first = parts[0] as ViewId | undefined;
  const view: ViewId = first && VIEWS.includes(first) && first !== 'today' ? first : 'today';
  const sub = view === 'today' ? [] : parts.slice(1);
  const search = new URLSearchParams(window.location.search);
  let record: RecordRef | null = null;
  const rec = search.get('record');
  if (rec) {
    const [type, id] = rec.split('/');
    if ((type === 'person' || type === 'company') && id) {
      record = { type, id, tab: search.get('tab') ?? undefined, focus: search.get('focus') ?? undefined };
    }
  }
  const admin = record ? null : search.get('admin');
  const params: Record<string, string> = {};
  for (const [k, v] of search) if (!RESERVED.has(k)) params[k] = v;
  return { view, sub, record, admin: admin || null, params };
}

export function buildUrl(route: Route): string {
  const path = route.view === 'today' ? '/' : `/${[route.view, ...route.sub].map(encodeURIComponent).join('/')}`;
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(route.params)) if (v !== '') search.set(k, v);
  if (route.record) {
    search.set('record', `${route.record.type}/${route.record.id}`);
    if (route.record.tab) search.set('tab', route.record.tab);
    if (route.record.focus) search.set('focus', route.record.focus);
  } else if (route.admin) search.set('admin', route.admin);
  const qs = search.toString().replace(/%2F/g, '/');
  return qs ? `${path}?${qs}` : path;
}

// Where you last were inside each view (an open conversation, a filter), so coming back to it picks up there.
const memory = new Map<ViewId, { sub: string[]; params: Record<string, string> }>();

export const routeStore = createStore<{ route: Route }>({ route: parseLocation() });

window.addEventListener('popstate', () => {
  const route = parseLocation();
  memory.set(route.view, { sub: route.sub, params: route.params });
  routeStore.set({ route });
});

export interface RoutePatch {
  view?: ViewId;
  sub?: string[];
  record?: RecordRef | null;
  admin?: string | null;
  /** Replaces the view's query parameters; pass null for a key to drop it. */
  params?: Record<string, string | null>;
}

/**
 * Works out the route a patch leads to. Moving to another view closes any open sheet and returns to where you
 * last were in that view, unless the patch says where to go inside it.
 */
export function resolve(patch: RoutePatch, from: Route = routeStore.get().route): Route {
  const changingView = patch.view !== undefined && patch.view !== from.view;
  const remembered = changingView && patch.sub === undefined && patch.params === undefined ? memory.get(patch.view!) : undefined;
  const base: Route = changingView
    ? { view: patch.view!, sub: remembered ? [...remembered.sub] : [], record: null, admin: null, params: remembered ? { ...remembered.params } : {} }
    : { ...from, params: { ...from.params } };
  if (patch.sub !== undefined) base.sub = patch.sub;
  if (patch.params) {
    for (const [k, v] of Object.entries(patch.params)) {
      if (v === null || v === '') delete base.params[k];
      else base.params[k] = v;
    }
  }
  if (patch.record !== undefined) {
    base.record = patch.record;
    if (patch.record) base.admin = null;
  }
  if (patch.admin !== undefined) {
    base.admin = patch.admin;
    if (patch.admin) base.record = null;
  }
  return base;
}

export function navigate(patch: RoutePatch, opts: { replace?: boolean } = {}): void {
  const current = routeStore.get().route;
  const next = resolve(patch, current);
  const url = buildUrl(next);
  const same = url === buildUrl(current);
  if (!same) {
    if (opts.replace) window.history.replaceState(null, '', url);
    else window.history.pushState(null, '', url);
  }
  memory.set(next.view, { sub: next.sub, params: next.params });
  routeStore.set({ route: next });
}

export function useRoute(): Route {
  return useStore(routeStore, (s) => s.route);
}

/** Props for a real link that navigates in place, while still opening in a new tab with a modifier key. */
export function useLink(patch: RoutePatch): { href: string; onClick: (e: MouseEvent) => void } {
  const route = useRoute();
  const href = buildUrl(resolve(patch, route));
  const key = JSON.stringify(patch);
  const onClick = useCallback(
    (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigate(JSON.parse(key) as RoutePatch);
    },
    [key],
  );
  return { href, onClick };
}

/** Back to Home with nothing open and no remembered places, used when the session ends. */
export function resetRoute(): void {
  memory.clear();
  window.history.replaceState(null, '', '/');
  routeStore.set({ route: parseLocation() });
}

/** Shows a view, closing any open sheet. Returning to the current view keeps its place and filters. */
export function goTo(view: ViewId): void {
  navigate({ view, record: null, admin: null });
}
